import { useEffect, useRef } from 'react';
import './ClubModal.css';
import {
  TIER_NAMES, IDENTITY_LINES, clubVars, initials, asHonours,
} from '../marketing/clubIdentity';

function Crest({ fill, stroke, ink, label }) {
  return (
    <svg className="cm-crest" viewBox="0 0 40 46" aria-hidden="true" focusable="false">
      <path
        d="M20 3 L36 9 L36 23 C36 35 29 41 20 44 C11 41 4 35 4 23 L4 9 Z"
        fill={fill} stroke={stroke} strokeWidth="1.6" strokeLinejoin="round"
      />
      <text x="20" y="27" textAnchor="middle" fontSize="12.5" fontWeight="700" fill={ink}
        style={{ fontFamily: 'var(--f-display)', letterSpacing: '.02em' }}>
        {label}
      </text>
    </svg>
  );
}

// Club identity card: big badge, heritage, grudges, honours. No squad/finances.
export default function ClubModal({ club, onClose, onPrev, onNext }) {
  const closeRef = useRef(null);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  if (!club) return null;

  const cv = clubVars(club.primary_color);
  const honours = asHonours(club.honours);
  const meta = [TIER_NAMES[club.tier], club.town, club.founded ? `Est. ${club.founded}` : null]
    .filter(Boolean).join(' · ');
  const groundLine = club.stadium
    ? `${club.stadium}${club.capacity ? `, ${Number(club.capacity).toLocaleString()}` : ''}`
    : null;
  const identity = IDENTITY_LINES[club.name]
    || [club.playing_style, club.owner_type].filter(Boolean).join(' · ');

  const onBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div className="cm-backdrop" onClick={onBackdrop} role="presentation">
      <button type="button" className="cm-nav prev" aria-label="Previous club" onClick={onPrev}>&#8249;</button>

      <div className="cm" role="dialog" aria-modal="true" aria-label={club.name}
        style={{ '--club': cv.border, '--club-glow': cv.glow, '--club-tint': cv.tint }}>
        <button type="button" className="cm-close" aria-label="Close" onClick={onClose} ref={closeRef}>&#215;</button>

        <div className="cm-content" key={club.id != null ? club.id : club.name}>
          <div className="cm-hd">
            <div className="cm-badge">
              {club.badge_url ? (
                <img src={club.badge_url} alt="" />
              ) : (
                <Crest
                  fill={club.primary_color || 'var(--bg-overlay)'}
                  stroke={cv.border}
                  ink={club.secondary_color || 'var(--tx-primary)'}
                  label={initials(club.name)}
                />
              )}
            </div>
            <div className="cm-name">{club.name}</div>
            {club.nickname && <div className="cm-nick">{club.nickname}</div>}
            {meta && <div className="cm-meta">{meta}</div>}
          </div>

          <div className="cm-body">
            {club.motto && <p className="cm-motto">&ldquo;{club.motto}&rdquo;</p>}
            {club.description && <p className="cm-desc">{club.description}</p>}
            {identity && <p className="cm-identity">{identity}</p>}

            <div className="cm-facts">
              {groundLine && (
                <div className="cm-fact"><span className="cm-fact-k">Home</span><span className="cm-fact-v">{groundLine}</span></div>
              )}
              {club.rivals && (
                <div className="cm-fact"><span className="cm-fact-k">Rivals</span><span className="cm-fact-v">{club.rivals}</span></div>
              )}
              {club.record_signing && (
                <div className="cm-fact"><span className="cm-fact-k">Record signing</span><span className="cm-fact-v">{club.record_signing}</span></div>
              )}
            </div>

            {honours.length > 0 && (
              <div className="cm-honours">
                <div className="cm-fact-k">Honours</div>
                <ul>{honours.map((h) => <li key={h}>{h}</li>)}</ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <button type="button" className="cm-nav next" aria-label="Next club" onClick={onNext}>&#8250;</button>
    </div>
  );
}
